import React, { useState, useEffect, useCallback } from 'react'
import { useAppSelector } from '@/app/hooks'
import { selectFilter } from '@/app/reducers/filters'
import { IFiltros, IAmounts } from '@/components/templates/search/interfaces/IEstructuraPrincipal'
import styles from './FiltroPreciosCustom.module.scss'

type OnPriceFilterChange = (filterId: string, newFilter: IFiltros | null) => void

interface FiltroPreciosCustomProps {
	filtroDefinicion: IFiltros
	onFilterChange: OnPriceFilterChange
}

type Currency = 'CLP' | 'UF'

const unformat = (v: string) => v.replace(/\./g, '')

const formatDisplay = (v: string): string => {
	const raw = unformat(v)
	const num = Number(raw)
	if (!raw || isNaN(num)) return v
	return num.toLocaleString('es-CL')
}

export const FiltroPreciosCustom = ({ filtroDefinicion, onFilterChange }: FiltroPreciosCustomProps) => {
	const filterState = useAppSelector(selectFilter)
	const selectedPreciosFilter = filterState.filtros.find(f => f.id === 'precios')

	const detectCurrency = (): Currency => {
		if (!selectedPreciosFilter) return 'UF'
		return selectedPreciosFilter.label === '$' ? 'CLP' : 'UF'
	}

	const [currency, setCurrency] = useState<Currency>(detectCurrency)
	const [fromValue, setFromValue] = useState('')
	const [toValue, setToValue] = useState('')

	// Sync state from Redux when filter changes externally (URL load, chip removal)
	useEffect(() => {
		const selected = filterState.filtros.find(f => f.id === 'precios')
		if (!selected) {
			setFromValue('')
			setToValue('')
			return
		}
		const detectedCurrency: Currency = selected.label === '$' ? 'CLP' : 'UF'
		setCurrency(detectedCurrency)

		const firstValue = selected.values?.[0]
		// id === 0 means custom typed range — pre-populate inputs
		if (firstValue?.id === 0) {
			const val = firstValue.value ?? []
			setFromValue(val[0] !== undefined ? String(val[0]) : '')
			setToValue(val[1] !== undefined && val[1] !== 'x' ? String(val[1]) : '')
		} else {
			setFromValue('')
			setToValue('')
		}
	}, [filterState.filtros])

	const currencyDef = filtroDefinicion.values.find(v =>
		currency === 'CLP' ? v.label === '$' : v.label === 'UF'
	)
	const ranges = currencyDef?.ranges ?? []

	// Which preset range is active (by id comparison, not by reference)
	const selectedRangeId = selectedPreciosFilter?.values?.[0]?.id

	const buildFilter = useCallback((newValues: IFiltros['values']): IFiltros => ({
		id: 'precios',
		type: filtroDefinicion.type,
		name: filtroDefinicion.name,
		hasFilterName: filtroDefinicion.hasFilterName,
		unit: 'moneda',
		label: currency === 'CLP' ? '$' : 'UF',
		values: newValues,
	}), [filtroDefinicion, currency])

	const handleCurrencySwitch = (newCurrency: Currency) => {
		setCurrency(newCurrency)
		setFromValue('')
		setToValue('')
		onFilterChange('precios', null)
	}

	const handleRangeClick = (range: IAmounts) => {
		if (selectedRangeId === range.id) {
			onFilterChange('precios', null)
			return
		}
		onFilterChange('precios', buildFilter([
			{ id: range.id ?? 0, label: '', value: range.value },
		]))
	}

	const handleCustomApply = () => {
		const rawFrom = unformat(fromValue)
		const rawTo = unformat(toValue)
		if (!rawFrom && !rawTo) return
		const from = Number(rawFrom)
		const to = rawTo ? Number(rawTo) : 'x'
		onFilterChange('precios', buildFilter([
			{ id: 0, label: '', value: [from, to], typed: true },
		]))
	}

	const handleFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const v = e.target.value.replace(/\./g, '')
		if (/^(?!0)\d*$/.test(v)) setFromValue(v)
	}

	const handleToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const v = e.target.value.replace(/\./g, '')
		if (/^(?!0)\d*$/.test(v)) setToValue(v)
	}

	const getRangeLabel = (range: IAmounts): string => {
		const sym = currency === 'CLP' ? '$' : 'UF'
		const from = Number(range.value[0]).toLocaleString('es-CL')
		if (range.value[1] === 'x') return `${sym} ${from} o más`
		const to = Number(range.value[1]).toLocaleString('es-CL')
		return `${sym} ${from} - ${sym} ${to}`
	}

	return (
		<div className={`${styles.container} filter-block f-radio`}>
			<div className={styles.header}>
				<span className={styles.filterName}>Precios</span>
				<div className={styles.switchRow}>
					<button
						className={`${styles.switchOption}${currency === 'CLP' ? ` ${styles.active}` : ''}`}
						onClick={() => handleCurrencySwitch('CLP')}
					>$</button>
					<button
						className={`${styles.switchOption}${currency === 'UF' ? ` ${styles.active}` : ''}`}
						onClick={() => handleCurrencySwitch('UF')}
					>UF</button>
				</div>
			</div>

			<div className={styles.rangeList}>
				{ranges.map(range => {
					const checked = selectedRangeId === range.id
					return (
						<div key={range.id} className={styles.rangeItem} onClick={() => handleRangeClick(range)}>
							<div className={`${styles.checkIcon}${checked ? ` ${styles.checked}` : ''}`}>
								{checked && (
									<svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path d="M2 6L5 9L10 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
									</svg>
								)}
							</div>
							<span className={styles.rangeLabel}>{getRangeLabel(range)}</span>
						</div>
					)
				})}
			</div>

			<div className={styles.inputRow}>
				<input
					type="text"
					className={styles.priceInput}
					placeholder="Desde"
					value={fromValue ? formatDisplay(fromValue) : ''}
					onChange={handleFromChange}
					onFocus={() => setFromValue(unformat(fromValue))}
					onBlur={() => setFromValue(v => v)}
				/>
				<span className={styles.inputSep}>—</span>
				<input
					type="text"
					className={styles.priceInput}
					placeholder="Hasta"
					value={toValue ? formatDisplay(toValue) : ''}
					onChange={handleToChange}
					onFocus={() => setToValue(unformat(toValue))}
					onBlur={() => setToValue(v => v)}
				/>
				<button
					type="button"
					className={styles.applyBtn}
					disabled={!fromValue && !toValue}
					onClick={handleCustomApply}
					aria-label="Aplicar rango"
				>
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
						<path d="M9 18l6-6-6-6" />
					</svg>
				</button>
			</div>
		</div>
	)
}
